import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Box, TextField, MenuItem } from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import AppSearchBar from "@crema/core/AppSearchBar";
import { AppState } from "redux/store";
import TransactionsTable from "./TransactionsTable";
import { getTransactions } from "redux/actions";
import { transactionsData } from "@crema/services/db/master/masterData";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
const Transactions: React.FC = () => {
  const dispatch = useDispatch();
  const { transactions, transactionCount } = useSelector<AppState, AppState["master"]>(
    ({ master }) => master
  );

  const [page, setPage] = useState(0);
  const [search, setSearchQuery] = useState<string>(""); 
  const [records, setRecords] = useState<string>("20"); 
  const [status, setStatus] = useState<string>("All");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  useEffect(() => {
    dispatch(getTransactions(search, page + 1));  
  }, [dispatch, search, page]);

  const onSearchTransaction = (value: string) => {
    setSearchQuery(value);
    setPage(0); 
  };
  const exportToCSV = () => {
    const csvRows: string[] = [];
    const headers = ['ID', 'orderId', 'txnId', 'txndate', 'custId', 'rmn', 'type', 'resMsg', 'wallet', 'refNo', 'amount', 'status'];
    csvRows.push(headers.join(','));

    transactionsData.forEach((transaction) => {
      const row = [
        transaction.id,
        transaction.orderId,
        transaction.txnId,
        transaction.txndate,
        transaction.custId,
        transaction.rmn,
        transaction.type,
        transaction.resMsg,
        transaction.wallet,
        transaction.refNo,
        transaction.amount,
        transaction.status,
      ];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'transactions.csv';
    link.click();
  };

  // Function for PDF Download
  const exportToPDF = () => {
    const doc = new jsPDF() as jsPDF & { autoTable: (options: any) => void };
  
    // Add Title
    doc.setFontSize(18);
    doc.text('Transactions Report', 14, 16);
  
    // Table Headers
    const headers = [
      ['ID', 'Order ID', 'Txn ID', 'Txn Date', 'Cust ID', 'RMN', 'Type', 'Res Msg', 'Wallet', 'Ref No', 'Amount', 'Status'],
    ];
  
    // Table Rows
    const rows = transactionsData.map((transaction) => [
      transaction.id,
      transaction.orderId,
      transaction.txnId,
      transaction.txndate,
      transaction.custId,
      transaction.rmn,
      transaction.type,
      transaction.resMsg,
      transaction.wallet,
      transaction.refNo,
      transaction.amount,
      transaction.status,
    ]);
  
    doc.autoTable({
      head: headers,
      body: rows,
      startY: 25, 
      theme: 'grid',
      headStyles: {
        fillColor: [22, 160, 133], 
        textColor: [255, 255, 255],
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 8,
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240], 
      },
      margin: { top: 20 },
    });
  
    // Save the PDF
    doc.save('transactions.pdf');
  };
  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              View Transactions
            </Box>
            <Button
              variant="outlined"
              color="secondary"
              onClick={exportToCSV} 
              sx={{ flexShrink: 0, marginRight: 2 }} 
            >
              Download CSV
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={exportToPDF} 
              sx={{ flexShrink: 0 }}
            >
              Download PDF
            </Button>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: 1,
              padding: 2,
            }}
          >
            <TextField
              select
              label="Records"
              variant="outlined"
              size="small"
              value={records}
              onChange={(e) => setRecords(e.target.value)}
              sx={{ width: 120, marginRight: 2 }}
            >
              <MenuItem value="20">20</MenuItem>
              <MenuItem value="50">50</MenuItem>
              <MenuItem value="100">100</MenuItem>
            </TextField>

            <TextField
              select
              label="Status"
              variant="outlined"
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ width: 120, marginRight: 2 }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
            </TextField>

            <TextField
              type="date"
              label="Start Date"
              variant="outlined"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ marginRight: 2 }}
            />

            <TextField
              type="date"
              label="End Date"
              variant="outlined"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ marginRight: 2 }}
            />

            <AppSearchBar
              iconPosition="right"
              overlap={false}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSearchTransaction(event.target.value)
              }
              placeholder="Search by Customer ID / RMN / Name"
              sx={{ flex: 1, marginRight: 2 }}
            />

            <Button
              variant="contained"
              color="primary"
              onClick={() => setPage(0)} 
            >
              Submit
            </Button>
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <TransactionsTable transactions={transactions} />
          <AppsPagination
            rowsPerPage={parseInt(records)} 
            count={transactionCount}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Transactions;
